import React, { useCallback } from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { theme, useResponsive } from '@sg/garnish';

import { OrderAgain, OrderLineItem, OrdersList } from '@order/features/orders';
import { useTelemetry } from '@order/Telemetry';

import { ReorderOrderLineItem } from '../../../components';
import { type LineItem, type Order } from '../utils';
import { OrdersEmptyState } from './OrdersEmptyState';
import { OrdersSeeMoreButton } from './OrdersSeeMoreButton';

export const OrdersListWrapper = (props: OrdersListWrapperProps) => {
  const {
    canReorder,
    orders,
    mostRecentOrder,
    isFetchingInitialOrders,
    isFetchingMoreOrders,
    canFetchMoreOrders,
    fetchMoreOrders,
  } = props;

  const navigation = useNavigation();
  const { track } = useTelemetry();
  const { match } = useResponsive();

  // ─── Helpers ─────────────────────────────────────────────────────────

  const navigateToOrderStatusScreen = React.useCallback(
    (orderId: string) => {
      navigation.navigate('Modal', {
        screen: 'OrderStatus',
        params: { orderId },
      });
    },
    [navigation],
  );

  const handleViewDetails = useCallback(
    (orderId: string) => {
      navigateToOrderStatusScreen(orderId);
      track('order_status.view', { entryPoint: 'account.orders' });
    },
    [navigateToOrderStatusScreen, track],
  );

  // ─────────────────────────────────────────────────────────────────────

  return (
    <ScrollView
      scrollEventThrottle={16}
      style={match([null, orders.length > 0 ? styles.containerBorder : null])}
      contentContainerStyle={styles.scrollView}
    >
      <OrdersList.Container style={styles.orderList}>
        {isFetchingInitialOrders ? (
          <View style={styles.loadingContainer}>
            <OrderAgain.LoadingPlaceholder />
          </View>
        ) : null}

        {!isFetchingInitialOrders && orders.length === 0 ? (
          <OrdersEmptyState />
        ) : null}

        {!isFetchingInitialOrders && orders.length > 0
          ? orders.map((order) => {
              const location =
                order.deliveryOrderDetail?.address?.name ??
                order.restaurant.name;

              return (
                <OrdersList.OrderContainer key={order.id}>
                  <OrdersList.OrderHeader
                    orderId={order.id}
                    date={order.wantedTime}
                    location={location}
                    onViewDetails={handleViewDetails}
                  />

                  <OrdersList.OrderLineItemsContainer>
                    {order.lineItems.map((lineItem) => {
                      return (
                        <LineItemWrapper
                          canReorder={canReorder}
                          key={lineItem.id}
                          lineItem={lineItem}
                          order={order}
                          mostRecentOrder={mostRecentOrder}
                        />
                      );
                    })}
                  </OrdersList.OrderLineItemsContainer>
                </OrdersList.OrderContainer>
              );
            })
          : null}

        {canFetchMoreOrders ? (
          <OrdersSeeMoreButton
            isFetchingMoreOrders={isFetchingMoreOrders}
            fetchMoreOrders={fetchMoreOrders}
          />
        ) : null}
      </OrdersList.Container>
    </ScrollView>
  );
};

const LineItemWrapper = (props: LineItemWrapperProps) => {
  const { lineItem, order, mostRecentOrder, canReorder } = props;

  if (canReorder) {
    return (
      <ReorderOrderLineItem
        key={lineItem.id}
        lineItem={lineItem}
        orderDetails={order}
        mostRecentOrder={mostRecentOrder}
      />
    );
  }

  return <OrderLineItem lineItem={lineItem} orderDetails={order} />;
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  containerBorder: {
    flexGrow: 0, // this is used to prevent the container (and the border) from taking the entire screen when there are only 1-3 orders.
    borderRadius: theme.radius.large,
    borderColor: theme.colors.GRAY,
    borderWidth: 1,
    overflow: 'hidden',
  },
  scrollView: {
    flexGrow: 1,
  },
  orderList: {
    marginTop: 0,
    marginBottom: 0,
  },
  loadingContainer: {
    marginVertical: theme.spacing['4'],
  },
});

type OrdersListWrapperProps = Readonly<{
  canReorder: boolean;
  orders: readonly Order[];
  mostRecentOrder: Order;
  isFetchingInitialOrders: boolean;
  isFetchingMoreOrders: boolean;
  canFetchMoreOrders: boolean;
  fetchMoreOrders: () => void;
}>;

type LineItemWrapperProps = Readonly<{
  canReorder: boolean;
  order: Order;
  lineItem: LineItem;
  mostRecentOrder: Order;
}>;
